// src/components/MeetingRoom/hooks/useMergedParticipants.js
import { useMemo, useRef } from "react";
import { Track } from "livekit-client";

export function useMergedParticipants({
    localParticipant,
    remoteParticipants,
    tracks,
    apiUserMap,
    apiParticipants,
    tableRoster,
    getRoleImageFromMetaOrApi,
    capacity = 8,
}) {
    const screenShareTrack = useMemo(() => {
        return (tracks || []).find(
            (t) =>
                t.source === Track.Source.ScreenShare &&
                t.publication != null &&
                (t.participant?.isLocal || t.publication?.isSubscribed)
        );
    }, [tracks]);

    const cameraMap = useMemo(() => {
        const m = new Map();
        (tracks || []).forEach((t) => {
            if (t?.source === Track.Source.Camera && t?.participant?.identity) {
                m.set(t.participant.identity, t);
            }
        });
        return m;
    }, [tracks]);

    const rosterById = useMemo(() => {
        const m = new Map();
        for (const u of tableRoster?.participants || []) {
            if (u?._id) m.set(String(u._id), u);
        }
        return m;
    }, [tableRoster?.participants]);

    const rosterByName = useMemo(() => {
        const m = new Map();
        for (const u of tableRoster?.participants || []) {
            if (u?.name) m.set(String(u.name).toLowerCase().trim(), u);
        }
        return m;
    }, [tableRoster?.participants]);

    const liveParticipants = useMemo(() => {
        const list = [];
        const lp = localParticipant ? [localParticipant] : [];
        const rp = Array.isArray(remoteParticipants) ? remoteParticipants : [];

        const all = [
            ...lp,
            ...rp.filter(
                (p) => p?.identity && p.identity !== localParticipant?.identity
            ),
        ];

        for (const p of all) {
            const { name, image, role, userId, networkingRole, networkingIntent } =
                getRoleImageFromMetaOrApi(p, apiUserMap, rosterById);

            let resolvedUserId = userId || null;

            if (!resolvedUserId && name) {
                const rosterHit = rosterByName.get(String(name).toLowerCase().trim());
                resolvedUserId = rosterHit?._id || null;
            }

            let resolvedNetworkingRole = networkingRole;
            let resolvedNetworkingIntent = networkingIntent;

            if (resolvedUserId) {
                const rosterUser = rosterById.get(String(resolvedUserId));
                if (rosterUser) {
                    resolvedNetworkingRole =
                        rosterUser.networkingRole ?? resolvedNetworkingRole;
                    resolvedNetworkingIntent =
                        rosterUser.networkingIntent ?? resolvedNetworkingIntent;
                }
            }

            const trackRef = cameraMap.get(p.identity) || null;
            const isMuted = p.isMicrophoneEnabled === false;
            const isCameraOn = p.isCameraEnabled !== false;
            const pub = trackRef?.publication ?? null;
            const hasVideo = !!(isCameraOn && pub?.track && pub?.isMuted === false);

            list.push({
                id: p.identity,
                identity: p.identity,
                name,
                image,
                role,
                userId: resolvedUserId,
                networkingRole: resolvedNetworkingRole,
                networkingIntent: resolvedNetworkingIntent,
                isMuted,
                isCameraOn,
                isSpeaking: !!p.isSpeaking,
                isLocal: !!p.isLocal,
                trackRef,
                hasVideo,
                isLive: true,
                isCameraOff: p.isCameraEnabled === false,
            });
        }

        return list;
    }, [
        localParticipant,
        remoteParticipants,
        apiUserMap,
        cameraMap,
        rosterById,
        rosterByName,
        getRoleImageFromMetaOrApi,
    ]);

    const rosterUsers = useMemo(() => {
        return Array.isArray(tableRoster?.participants)
            ? tableRoster.participants
            : [];
    }, [tableRoster?.participants]);

    const liveUserIdSet = useMemo(() => {
        const s = new Set();
        for (const p of liveParticipants) {
            if (p.userId) s.add(String(p.userId));
        }
        return s;
    }, [liveParticipants]);

    const liveNameSet = useMemo(() => {
        const s = new Set();
        for (const p of liveParticipants) {
            if (p.name) s.add(String(p.name).toLowerCase().trim());
        }
        return s;
    }, [liveParticipants]);

    const pendingApi = useMemo(() => {
        return rosterUsers
            .filter((p) => {
                if (!p?._id) return false;
                if (liveUserIdSet.has(String(p._id))) return false;
                const nameLower = String(p.name || "")
                    .toLowerCase()
                    .trim();
                if (nameLower && liveNameSet.has(nameLower)) return false;
                return true;
            })
            .map((p) => ({
                id: `api-${p._id}`,
                identity: null,
                userId: p._id,
                name: p.name || "Unknown",
                image: p.image || null,
                role: p.role || "participant",
                networkingRole: p.networkingRole || null,
                networkingIntent: p.networkingIntent || null,
                isMuted: true,
                isCameraOn: false,
                isSpeaking: false,
                isLocal: !!p.isCurrentUser,
                trackRef: null,
                hasVideo: false,
                isLive: false,
                isCameraOff: true,
            }));
    }, [rosterUsers, liveUserIdSet, liveNameSet]);

    const allParticipants = useMemo(() => {
        const seenUserIds = new Set();
        const seenIds = new Set();
        const result = [];

        for (const p of [...liveParticipants, ...pendingApi]) {
            if (p.userId) {
                if (seenUserIds.has(String(p.userId))) continue;
                seenUserIds.add(String(p.userId));
            }
            if (seenIds.has(p.id)) continue;
            seenIds.add(p.id);
            result.push(p);
        }

        return result;
    }, [liveParticipants, pendingApi]);

    const liveOnlyParticipants = useMemo(() => {
        return allParticipants.filter((p) => p.isLive);
    }, [allParticipants]);

    const liveParticipantListForUI = useMemo(() => {
        const seenUserIds = new Set();
        const seenIdentities = new Set();
        const result = [];

        for (const p of liveOnlyParticipants) {
            if (p.userId) {
                if (seenUserIds.has(String(p.userId))) continue;
                seenUserIds.add(String(p.userId));
            }
            if (p.identity) {
                if (seenIdentities.has(p.identity)) continue;
                seenIdentities.add(p.identity);
            }
            result.push({
                id: p.id,
                identity: p.identity,
                userId: p.userId,
                name: p.name,
                image: p.image,
                role: p.role,
                networkingRole: p.networkingRole,
                networkingIntent: p.networkingIntent,
                isMuted: p.isMuted,
                isCameraOff: !p.isCameraOn,
                isLocal: p.isLocal,
                isLive: true,
            });
        }

        return result;
    }, [liveOnlyParticipants]);

    const participantListForModal = useMemo(() => {
        return allParticipants.map((p) => ({
            id: p.id,
            name: p.name,
            image: p.image,
            role: p.role,
            networkingRole: p.networkingRole,
            networkingIntent: p.networkingIntent,
            isMuted: p.isMuted,
            isCameraOff: !p.isCameraOn,
            isLocal: p.isLocal,
            isLive: p.isLive,
        }));
    }, [allParticipants]);

    const presenters = useMemo(() => {
        return liveOnlyParticipants
            .filter((p) => p.role === "host" || p.role === "speaker")
            .sort((a, b) => (a.role === "host" ? -1 : 1));
    }, [liveOnlyParticipants]);

    const regularParticipants = useMemo(() => {
        return allParticipants.filter(
            (p) => p.isLive && p.role !== "host" && p.role !== "speaker"
        );
    }, [allParticipants]);

    const activeSpeaker = useMemo(() => {
        return (
            liveOnlyParticipants.find((p) => p.isSpeaking && !p.isLocal) || null
        );
    }, [liveOnlyParticipants]);

    // ✅ Dynamic seat count based on capacity
    const seatCount = capacity <= 10 ? capacity : 8; // 50-person uses gallery, not seats

    const seats = useMemo(() => {
        const arr = new Array(seatCount).fill(null);
        for (let i = 0; i < seatCount; i++)
            arr[i] = regularParticipants[i] ?? null;
        return arr;
    }, [regularParticipants, seatCount]);

    const overflowCount = Math.max(0, regularParticipants.length - seatCount);

    const persistentNameCacheRef = useRef({});

    const nameByIdentity = useMemo(() => {
        const m = {};
        for (const p of allParticipants) {
            if (p.identity && p.name) {
                m[p.identity] = p.isLocal ? "You" : p.name;
                persistentNameCacheRef.current[p.identity] = p.isLocal
                    ? "You"
                    : p.name;
            }
        }
        return { ...persistentNameCacheRef.current, ...m };
    }, [allParticipants]);

    return {
        screenShareTrack,
        cameraMap,
        liveParticipants,
        pendingApi,
        allParticipants,
        liveOnlyParticipants,
        liveParticipantListForUI,
        participantListForModal,
        presenters,
        regularParticipants,
        activeSpeaker,
        seats,
        overflowCount,
        nameByIdentity,
    };
}